/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback, useMemo} from 'react';
import Modal from '@material-ui/core/Modal';
import {translate} from '@ion/core/src/locale/locale';
import {MaterialExitCloseIcon} from '../../icons/MaterialExitCloseIcon';
import TopNavSearchIcon from '../../icons/TopNavSearchIcon';
import Render from '../../render/Render';
import {dlsColor} from '../../theme/theme';
import {IconSize} from '../../types/types';
import {Option} from './AutocompleteSearch';
import {useSearchOverlayStyles} from './SearchOverlay.styles';
import SearchOverlayPanel from './SearchOverlayPanel';
import {getSearchOverlayPanelState} from './SearchOverlayUtils';
import useSearchOverlayInput from './useSearchOverlayInput';

export interface SearchOverlayProps {
  loading?: boolean;
  // the shortest term that is searched - a shorter one shows a hint ...
  minSearchLength?: number;
  onChange: (value: string) => void;
  onClose: () => void;
  onRecentSearchRemove?: (option: Option) => void;
  onResultSelect: (value: Option) => void;
  open: boolean;
  options: Option[];
  placeholder?: string;
  recentSearches?: Option[];
  title?: string;
}

/**
 * Displays a scrim-backed search overlay with recent searches and results.
 *
 * ```typescript
 * import SearchOverlay from '@ion/dls/src/components/search/SearchOverlay';
 *
 * ...
 *
 * <SearchOverlay open={open}
 *   options={searchResults}
 *   recentSearches={searchHistory}
 *   onChange={setSearchText}
 *   onClose={closeSearch}
 *   onResultSelect={selectResult} />
 * ```
 *
 * @param props Defines search results, history, and interaction callbacks.
 * @returns The overlay search experience.
 */
export const SearchOverlay = ({
  loading = false,
  minSearchLength = 0,
  onChange,
  onClose,
  onRecentSearchRemove,
  onResultSelect,
  open,
  options,
  placeholder = translate('common:search.searchPlaceholderTracking'),
  recentSearches = [],
  title = translate('common:search.searchTracking')
}: SearchOverlayProps) => {
  const classes: any = useSearchOverlayStyles();
  const {
    handleClose,
    handleInputChange,
    handleSearchKeyDown,
    inputRef,
    searchValue
  } = useSearchOverlayInput({
    minSearchLength,
    onChange,
    onClose,
    open
  });

  const {
    displayResults,
    hasPanel,
    hasResults,
    panelTitle,
    resultCounter,
    searchLoading,
    stateText
  } = useMemo(() => getSearchOverlayPanelState({
    loading,
    minSearchLength,
    options,
    recentSearches,
    searchValue,
    translate
  }), [loading, minSearchLength, options, recentSearches, searchValue]);

  const handleResultClick = useCallback((option: Option) => {
    onResultSelect(option);
    handleClose();
  }, [handleClose, onResultSelect]);

  return (
    <Modal open={open}
      className={classes.root}
      BackdropProps={{
        classes : {
          root : classes.backdrop
        }
      }}
      onClose={handleClose}>
      <div className={classes.container}>
        <div className={classes.titleContainer}>
          <span className={classes.title}>{title}</span>
        </div>
        <div className={classes.searchStack}>
          <div className={classes.searchBox}>
            <div className={classes.inputGroup}>
              <TopNavSearchIcon size={IconSize.medium} color={dlsColor('grey1')} />
              <input ref={inputRef}
                className={classes.input}
                value={searchValue}
                placeholder={placeholder}
                onChange={handleInputChange}
                onKeyDown={handleSearchKeyDown}
                aria-label={title} />
            </div>
            <button type='button'
              className={classes.closeButton}
              aria-label={translate('common:close')}
              onClick={handleClose}>
              <MaterialExitCloseIcon size={IconSize.small} color={dlsColor('grey1')} />
            </button>
          </div>
          <Render.If condition={hasPanel}>
            <SearchOverlayPanel classes={classes}
              displayResults={displayResults}
              hasResults={hasResults}
              onResultClick={handleResultClick}
              onRecentSearchRemove={onRecentSearchRemove}
              panelTitle={panelTitle}
              resultCounter={resultCounter}
              searchLoading={searchLoading}
              searchValue={searchValue}
              stateText={stateText} />
          </Render.If>
        </div>
      </div>
    </Modal>
  );
};

export default memo(SearchOverlay);
